import _ from "lodash";
import React, { useRef, useState, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Detector } from "react-detect-offline";

import { IconButton } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";

import {
  checkScrollAtBottom,
  prepareLoadMoreFeed,
  isUserTypeStaff,
  getGlobalTimezone,
} from "Utils";
import { compose, graphql } from "lib/apolloClient";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { STAFF } from "Constants/stringConstants";
import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import {
  getChatListQueryVariables,
  getFormattedNameForUserType,
  getSubText,
  isInitiateMessageEnabled,
  getIsViewMode,
  ORDERED_USER_TYPES,
  isMessagingRevempEnabled,
  getMessageTimeSinceText,
} from "MessagingV2/modules/Utils";
import {
  useChatOptions,
  withChatOptions,
} from "MessagingV2/providers/ChatOptionsProvider";

import Thread from "./Thread/Thread";
import EmptyChatList, { EMPTY_CHAT_LIST_VARIANTS } from "./EmptyChatList";
import UserTypeFilter from "./UserTypeFilter/UserTypeFilter";
import CreateNewChatModal from "./CreateNewChatModal";
import classes from "./ChatList.scss";
import {
  getChatListQuery,
  getAllChatListQuery,
} from "./Utils/ThreadListQueries";
import {
  getChatListFromCache,
  getAllChatListFromCache,
} from "./Utils/ThreadListGraphqlHelpers";
import HeaderV2 from "../HeaderV2";

const areAllFiltersSelected = userTypeFilters => {
  if (_.isEmpty(userTypeFilters)) return true;
  const userFilterSet = new Set(userTypeFilters);
  return _.every(ORDERED_USER_TYPES, userType => userFilterSet.has(userType));
};

const ChatList = props => {
  const {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    chatId,
    threads,
    onThreadClick,
    userType,
    refetchChatList,
    loadMoreChats,
    hasNextPage,
    getChatList,
    userTypeFilters,
    input,
  } = props;

  const containerRef = useRef();
  const searchText = _.get(input, "search.text");

  const handleScroll = () => {
    if (
      hasNextPage &&
      getChatList?.networkStatus !== 3 &&
      containerRef.current &&
      checkScrollAtBottom(containerRef.current)
    ) {
      // get next list of chats
      loadMoreChats();
    }
  };
  const { t } = useI18n(["common"]);
  const handleNetworkChange = isOnline => {
    if (isOnline && _.isFunction(refetchChatList)) refetchChatList();
  };

  const isNoFilterState =
    areAllFiltersSelected(userTypeFilters) || userType !== STAFF;

  const onThreadClickHandler = useCallback(id => onThreadClick(id), [
    onThreadClick,
  ]);

  return (
    <>
      <Detector
        polling={false}
        render={() => null}
        onChange={handleNetworkChange}
      />

      <div
        className={classes.chatMessagesContainer}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {_.size(threads) > 0 ? (
          _.map(
            threads,
            ({
              id,
              isMuted,
              lastActiveAt,
              unreadMessageCount,
              personalChatReceiverUser: messagedUser,
            }) => {
              if (!messagedUser) return null;
              const { profileImage } = messagedUser;

              return (
                <Thread
                  id={id}
                  avatarId={_.get(messagedUser, "id")}
                  key={id}
                  isMainTextHighlighted={unreadMessageCount > 0 && !isMuted}
                  mainText={getFormattedNameForUserType({
                    user: messagedUser,
                    orderBy: defaultStudentNameSortPreference,
                    defaultStudentNameOrderPreference,
                    isFormattedNameV2: true,
                  })}
                  subText={getSubText({
                    loggedInUserRole: userType,
                    messagedUserObj: messagedUser,
                    messagedUserRole: _.get(messagedUser, "type"),
                    t,
                    defaultStudentNameSortPreference,
                  })}
                  unreadMessageCount={unreadMessageCount}
                  timeSinceLastMessage={getMessageTimeSinceText(
                    lastActiveAt,
                    t
                  )}
                  isChatMuted={isMuted}
                  selected={chatId == id}
                  isArchived={_.get(messagedUser, "isArchived")}
                  onlineStatus={false}
                  profileImage={profileImage}
                  onThreadClick={onThreadClickHandler}
                />
              );
            }
          )
        ) : (
          <EmptyChatList
            variant={
              searchText
                ? EMPTY_CHAT_LIST_VARIANTS.SEARCH
                : isNoFilterState
                ? EMPTY_CHAT_LIST_VARIANTS.NO_FILTERS
                : EMPTY_CHAT_LIST_VARIANTS.APPLIED_FILTERS
            }
            searchText={searchText}
            userTypeFilters={userTypeFilters}
          />
        )}
        {getChatList?.networkStatus == 3 ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : null}
      </div>
    </>
  );
};

const ChatListWithData = compose(
  graphql(getChatListQuery, {
    name: "getChatList",
    skip: ({ isViewMode }) => isViewMode,
    options: ({ userTypeFilters, input = {} }) => ({
      fetchPolicy: "cache-and-network",
      variables: getChatListQueryVariables({
        input: { userType: userTypeFilters, ...input },
      }),
    }),
    props({ getChatList }) {
      const { variables } = getChatList;
      const chatResult = getChatListFromCache(variables);

      const pageInfo = _.get(chatResult, "node.chats.pageInfo", {});
      const hasNextPage = _.get(
        chatResult,
        "node.chats.pageInfo.hasNextPage",
        false
      );

      const threads = _.map(_.get(chatResult, "node.chats.edges", []), chat =>
        _.get(chat, "node")
      );

      return {
        isData: !_.isEmpty(chatResult),
        isLoading: _.includes([1, 2], getChatList["networkStatus"]),
        threads,
        hasNextPage,
        getChatList,
        refetchChatList: getChatList.refetch,
        loadMoreChats: prepareLoadMoreFeed({
          variables: {
            ...variables,
            input: {
              ...variables.input,
              after: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getChatList,
          key: "chats",
        }),
      };
    },
  }),
  graphql(getAllChatListQuery, {
    name: "getAllChatList",
    skip: ({ isViewMode }) => !isViewMode,
    options: ({ userTypeFilters, userId, input = {} }) => ({
      fetchPolicy: "cache-and-network",
      variables: getChatListQueryVariables({
        input: {
          userType: userTypeFilters,
          userId,
          ...input,
        },
      }),
    }),
    props({ getAllChatList }) {
      const { variables } = getAllChatList;
      const chatResult = getAllChatListFromCache(variables);

      const pageInfo = _.get(chatResult, "node.allChats.pageInfo", {});
      const hasNextPage = _.get(
        chatResult,
        "node.allChats.pageInfo.hasNextPage",
        false
      );

      const threads = _.map(
        _.get(chatResult, "node.allChats.edges", []),
        chat => _.get(chat, "node")
      );

      return {
        isData: !_.isEmpty(chatResult),
        isLoading: _.includes([1, 2], getAllChatList["networkStatus"]),
        threads,
        hasNextPage,
        refetchChatList: getAllChatList.refetch,
        getAllChatList,
        loadMoreChats: prepareLoadMoreFeed({
          variables: {
            ...variables,
            input: {
              ...variables.input,
              after: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getAllChatList,
          key: "allChats",
        }),
      };
    },
  }),
  withLoader
)(ChatList);

const ChatListWrapper = props => {
  const {
    showUserTypeFilterAndInitiateChat = true,
    userTypeFilters,
    setUserTypeFilters,
    isUserStaff,
  } = props;
  const { screenMode } = useChatOptions();

  const [isCreateNewChatModalOpen, setIsCreateNewChatModalOpen] = useState(
    false
  );

  const isInitiateChatEnable = isInitiateMessageEnabled();
  const isViewMode = getIsViewMode({ screenMode });

  const addNewChat = () => {
    setIsCreateNewChatModalOpen(true);
  };

  const closeCreateNewChatModal = () => {
    setIsCreateNewChatModalOpen(false);
  };

  const showInitiateChat = isInitiateChatEnable && !isViewMode;

  const showFilterHeader = showUserTypeFilterAndInitiateChat;

  const [searchText, setSearchText] = useState("");

  const chatListInput = useMemo(
    () => ({ search: { text: _.trim(searchText) } }),
    [searchText]
  );

  return (
    <>
      {showFilterHeader ? (
        isMessagingRevempEnabled() ? (
          <HeaderV2
            isUserStaff={isUserStaff}
            showInitiateChat={showInitiateChat}
            addNewChat={addNewChat}
            setSearchText={setSearchText}
          />
        ) : (
          <div className={classes.filterHeaderContainer}>
            {isUserStaff ? (
              <UserTypeFilter
                values={userTypeFilters}
                onOptionsSelected={setUserTypeFilters}
              />
            ) : null}

            {showInitiateChat ? (
              <div className={classes.iconBtn}>
                <IconButton
                  icon={<AddOutlined />}
                  size="x-small"
                  onClick={addNewChat}
                  variant="progressive"
                />
              </div>
            ) : null}
          </div>
        )
      ) : showUserTypeFilterAndInitiateChat && !isMessagingRevempEnabled() ? (
        <div className={classes.separator} />
      ) : null}
      <ChatListWithData
        {...props}
        {...(_.trim(searchText) && { input: chatListInput })}
      />
      {isCreateNewChatModalOpen ? (
        <CreateNewChatModal onCloseModal={closeCreateNewChatModal} />
      ) : null}
    </>
  );
};

ChatListWrapper.propTypes = {
  unreadMessageCount: PropTypes.number,
  timeSinceLastMessage: PropTypes.string,
  isMuted: PropTypes.bool,
  onThreadClick: PropTypes.func,
  userTypeFilters: PropTypes.array,
  setUserTypeFilters: PropTypes.func,
  refetchChatList: PropTypes.func,
};

ChatListWrapper.defaultProps = {
  userTypeFilters: [],
  setUserTypeFilters: () => {},
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userId = _.get(ownProps, "activeUserId");
  const userType = userInfo.user_type;
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const isUserStaff = isUserTypeStaff({ userType });

  return {
    userId,
    organizationId: userInfo.org_id,
    isUserStaff,
    userType,
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    currentTimezone: getGlobalTimezone(),
  };
};

export default compose(
  withRouter,
  withChatOptions(),
  connect(mapStateToProps),
  memo
)(ChatListWrapper);
